/**
 * MedCom Certificerings Workspace
 *
 * Forenklet UI til MedCom certificeringstest og dokumentation.
 * Viser alle påkrævede felter tydeligt mærket til ITB-test og screenshots.
 * Efter certificering integreres funktionaliteten i produktions-UI.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useActiveClinic } from '@/lib/activeClinic';
import { FilterChip } from '@/components/ui/Chip';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { useCareCommunicationsList } from '@/hooks/useCareCommunications';
import { useCareCommunicationReads } from '@/hooks/useCareCommunicationReads';
import { MedComSendForm } from './MedComSendForm';
import { MedComInbox } from './MedComInbox';
import { MedComSent } from './MedComSent';

type Tab = 'send' | 'inbox' | 'sent';

const TEST_TABS: Array<{ id: Tab; labelKey: 'test_send' | 'test_inbox' | 'test_sent' }> = [
  { id: 'send', labelKey: 'test_send' },
  { id: 'inbox', labelKey: 'test_inbox' },
  { id: 'sent', labelKey: 'test_sent' },
];

export function MedComCertification(_props: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('send');
  const { activeClinicId } = useActiveClinic();
  const inbox = useCareCommunicationsList(activeClinicId, { direction: 'in', status: 'all' });
  const { isUnread } = useCareCommunicationReads(activeClinicId, inbox.data?.items);
  const unreadCount = (inbox.data?.items ?? []).filter(isUnread).length;

  // Auto-switch to 'send' tab when action param is present (reply/forward)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'reply' || action === 'forward') {
      setActiveTab('send');
    }
  }, [searchParams]);

  const inner = (
    <div>
      <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center min-h-10">
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('medcomSection.title')}</h1>
        </div>
        <div
          className="flex flex-wrap items-center gap-s2"
          role="group"
          aria-label={t('medcomSection.tabs.test')}
        >
          {TEST_TABS.map((tab) => (
            <FilterChip
              key={tab.id}
              pressed={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {t(`medcomSection.${tab.labelKey}`)}
              {tab.id === 'inbox' && unreadCount > 0 ? (
                <span className="ml-s2 inline-flex min-w-5 items-center justify-center rounded-full bg-ds-amber-tint px-s2 text-ds-amber-ink">
                  {unreadCount}
                </span>
              ) : null}
            </FilterChip>
          ))}
        </div>
      </div>

      <div className="mt-s5">
        {activeTab === 'send' && <MedComSendForm />}
        {activeTab === 'inbox' && (
          <MedComInbox
            clinicId={activeClinicId}
            onReply={(id, messageId) => {
              navigate(
                `/medcom/test?action=reply&threadId=${encodeURIComponent(id)}&messageId=${encodeURIComponent(messageId)}`,
              );
            }}
          />
        )}
        {activeTab === 'sent' && <MedComSent clinicId={activeClinicId} />}
      </div>
    </div>
  );

  return inner;
}
